import React from 'react';

const styles = {
  page: {
    minHeight: '100vh',
    background: '#FDF8F5',
    display: 'flex',
    flexDirection: 'column',
  },
  nav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 48px',
  },
  logo: {
    fontSize: '28px',
    fontFamily: "'Playfair Display', serif",
    fontStyle: 'italic',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #F7C948, #F08080, #C3A8E1)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  navBtn: {
    background: 'linear-gradient(135deg, #F7C948 0%, #F4A261 100%)',
    color: '#2D2D2D',
    padding: '12px 28px',
    borderRadius: '999px',
    fontWeight: 600,
    fontSize: '15px',
    boxShadow: '0 4px 12px rgba(247,201,72,0.35)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  hero: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '60px 24px',
  },
  badge: {
    display: 'inline-block',
    background: 'rgba(195,168,225,0.2)',
    color: '#9B7EC8',
    padding: '8px 20px',
    borderRadius: '999px',
    fontSize: '13px',
    fontWeight: 500,
    marginBottom: '32px',
    border: '1px solid rgba(195,168,225,0.3)',
  },
  headline: {
    fontSize: '64px',
    fontFamily: "'Playfair Display', serif",
    fontWeight: 600,
    lineHeight: 1.15,
    marginBottom: '24px',
    maxWidth: '700px',
  },
  headlineAccent: {
    fontStyle: 'italic',
    background: 'linear-gradient(135deg, #F7C948, #F08080)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subtitle: {
    fontSize: '20px',
    color: '#6B6B6B',
    lineHeight: 1.7,
    maxWidth: '520px',
    marginBottom: '48px',
  },
  ctaGroup: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  primaryBtn: {
    background: 'linear-gradient(135deg, #F7C948 0%, #F4A261 100%)',
    color: '#2D2D2D',
    padding: '18px 40px',
    borderRadius: '999px',
    fontWeight: 700,
    fontSize: '17px',
    boxShadow: '0 8px 24px rgba(247,201,72,0.4)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
    border: 'none',
  },
  secondaryBtn: {
    background: 'transparent',
    color: '#6B6B6B',
    padding: '18px 32px',
    borderRadius: '999px',
    fontWeight: 500,
    fontSize: '16px',
    border: '2px solid #EDE8E3',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  features: {
    display: 'flex',
    gap: '24px',
    justifyContent: 'center',
    padding: '80px 48px',
    flexWrap: 'wrap',
  },
  featureCard: {
    background: '#FFFFFF',
    borderRadius: '28px',
    padding: '40px',
    maxWidth: '300px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
    textAlign: 'left',
    flex: '1 1 260px',
  },
  featureIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    marginBottom: '20px',
  },
  featureTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '12px',
    color: '#2D2D2D',
  },
  featureText: {
    fontSize: '15px',
    color: '#6B6B6B',
    lineHeight: 1.7,
  },
  footer: {
    textAlign: 'center',
    padding: '32px',
    color: '#A0A0A0',
    fontSize: '14px',
    borderTop: '1px solid #EDE8E3',
  },
};

const features = [
  {
    icon: '🎙️',
    color: '#FFF3CD',
    title: 'Voice Journaling',
    text: 'Speak your thoughts. Life Map transcribes and saves your voice entries automatically.',
  },
  {
    icon: '🧠',
    color: '#EDE8F7',
    title: 'AI Companion',
    text: 'An AI that actually knows your story — reads your past, spots patterns, and grows with you.',
  },
  {
    icon: '🌈',
    color: '#E8F7ED',
    title: 'Mood Mapping',
    text: 'Track how you feel over time. Discover what lifts you up and what drags you down.',
  },
  {
    icon: '💡',
    color: '#FDE8E8',
    title: 'Life Insights',
    text: 'Weekly reflections that connect the dots of your journey — career, relationships, growth.',
  },
];

export default function LandingPage({ navigate }) {
  return (
    <div style={styles.page}>
      <nav style={styles.nav}>
        <div style={styles.logo}>lifemap</div>
        <button style={styles.navBtn} onClick={() => navigate('dashboard')}>
          Open App →
        </button>
      </nav>

      <div style={styles.hero}>
        <div style={styles.badge}>✨ Your AI life companion</div>
        <h1 style={styles.headline}>
          Map your life,<br />
          <span style={styles.headlineAccent}>one moment at a time</span>
        </h1>
        <p style={styles.subtitle}>
          Life Map is a personal journal with an AI companion that truly knows you.
          Voice entries, mood tracking, and deep insights — all in one warm place.
        </p>
        <div style={styles.ctaGroup}>
          <button style={styles.primaryBtn} onClick={() => navigate('dashboard')}>
            Start your journey
          </button>
          <button style={styles.secondaryBtn} onClick={() => navigate('companion')}>
            Talk to your companion
          </button>
        </div>
      </div>

      <div style={styles.features}>
        {features.map((f, i) => (
          <div key={i} style={styles.featureCard}>
            <div style={{ ...styles.featureIcon, background: f.color }}>
              {f.icon}
            </div>
            <div style={styles.featureTitle}>{f.title}</div>
            <div style={styles.featureText}>{f.text}</div>
          </div>
        ))}
      </div>

      <footer style={styles.footer}>
        © 2026 Life Map · Your story, mapped with care 🧶
      </footer>
    </div>
  );
}
